import { Component, DestroyRef, inject, OnDestroy, OnInit, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../core/services/auth.service';
import { AtencionService } from '../../core/services/atencion.service';
import { PacienteService } from '../../core/services/pacientes.service';
import { Paciente, AtencionDashboard } from '../pacientes/pacientes';
import { MatIcon } from '@angular/material/icon';
import { DashboardCards } from './dashboard-cards/dashboard-cards';
import { dashboadDetail } from './dashboard-detail/dashboard-detail';
import { MatInputModule, MatLabel } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { DashboardAtendidos } from './dashboard-atendidos/dashboard-atendidos';
import { DashboardPendientes } from './dashboard-pendientes/dashboard-pendientes';
import { NuevoPaciente } from '../pacientes/nuevo-paciente/nuevo-paciente';
import { RegistroSignosVitales } from '../pacientes/registro-signos-vitales/registro-signos-vitales';
import { debounceTime, Subscription, tap, merge } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SocketService } from '../../core/services/shoket.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule, MatIconButton } from '@angular/material/button';

@Component({
  selector: 'app-inicio',
  templateUrl: './inicio.html',
  styleUrl: './inicio.scss',
  imports: [
    MatIcon,
    MatIconButton,
    DashboardCards,
    dashboadDetail,
    MatFormFieldModule,
    MatLabel,
    FormsModule,
    MatInputModule,
    MatSnackBarModule,
    MatButtonModule,
  ],
})
export class Inicio implements OnInit, OnDestroy {
  private socket = inject(SocketService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private pacienteService: PacienteService,
    private atencionService: AtencionService,
    private snackBar: MatSnackBar,
  ) {}
  private usuarioSubscription?: Subscription;

  mostrarResultadosBusqueda = false;

  alEscribirBusqueda(): void {
    this.mostrarResultadosBusqueda = true;
    this.filtrarPacientes();
  }

  @HostListener('document:click', ['$event'])
  cerrarBusquedaAlHacerClickFuera(event: MouseEvent): void {
    const objetivo = event.target;

    if (!(objetivo instanceof Element)) {
      return;
    }

    // Los clics dentro del buscador o sus resultados no lo cierran.
    const dentroDelBuscador = objetivo.closest('.buscar-paciente-panel');

    if (!dentroDelBuscador) {
      this.mostrarResultadosBusqueda = false;
    }
  }

  user: any;

  pacientes: Paciente[] = [];
  pacientesFiltrados: Paciente[] = [];

  busqueda = '';

  get pendientes(): AtencionDashboard[] {
    return this.atenciones;
  }

  get consultasHoy(): number {
    return this.atencionesEnConsulta.length;
  }

  get restantes(): number {
    return this.atenciones.length;
  }

  get proximaConsulta(): AtencionDashboard | null {
    if (this.atenciones.length === 0) {
      return null;
    }

    return this.atenciones[0];
  }

  atenciones: AtencionDashboard[] = [];

  atencionesEnConsulta: AtencionDashboard[] = [];

  atencionesFinalizadas: AtencionDashboard[] = [];

  ngOnInit(): void {
    this.usuarioSubscription = this.authService.usuario$.subscribe((usuario) => {
      if (!usuario) return;

      this.user = usuario;
      // this.socket.connect(this.authService.getToken()!); // ← ver nota abajo
      this.cargarPacientes();
    });

    this.escucharEventosWs();
  }

  private escucharEventosWs(): void {
    const atencionCreada$ = this.socket.on<any>('atencion.creada');
    const atencionActualizada$ = this.socket.on<any>('atencion.actualizada');
    const atencionEstatus$ = this.socket.on<any>('atencion.estatus');

    merge(atencionCreada$, atencionActualizada$, atencionEstatus$)
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.esPersonalDashboard()) {
          this.refrescarAtenciones();
        }
      });

    const pacienteCreado$ = this.socket.on<any>('paciente.creado');
    const pacienteActualizado$ = this.socket.on<any>('paciente.actualizado');
    const pacienteEliminado$ = this.socket.on<any>('paciente.eliminado');

    merge(pacienteCreado$, pacienteActualizado$, pacienteEliminado$)
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refrescarPacientes());
  }

  private refrescarAtenciones(): void {
    this.cargarAtencionesEspera();
    this.cargarAtencionesEnConsulta();
  }

  private refrescarPacientes(): void {
    this.pacienteService.obtenerPacientes().subscribe({
      next: (pacientes) => {
        this.pacientes = pacientes;
        this.filtrarPacientes();
      },
      // error: (error) => console.error('Error al refrescar pacientes:', error),
    });
  }

  limpiarBusqueda(input?: HTMLInputElement): void {
    this.busqueda = '';
    this.pacientesFiltrados = [...this.pacientes];
    this.mostrarResultadosBusqueda = false;

    input?.focus();
  }

  ngOnDestroy(): void {
    this.usuarioSubscription?.unsubscribe();
  }

  tieneRol(rol: string): boolean {
    return this.user?.rol?.nombre?.trim().toUpperCase() === rol;
  }

  // =========================================================
  // PACIENTES
  // =========================================================

  cargarPacientes(): void {
    this.pacienteService.obtenerPacientes().subscribe({
      next: (pacientes) => {
        // console.log('========== PACIENTES DEL BACKEND ==========');
        // console.log(pacientes);

        this.pacientes = pacientes;
        this.pacientesFiltrados = [...pacientes];

        if (this.esPersonalDashboard()) {
          this.cargarAtenciones();
        }
      },

      error: (error) => {
        // console.error('Error al obtener pacientes:', error);
      },
    });
  }

  esPersonalDashboard(): boolean {
    return this.tieneRol('MEDIC@') || this.tieneRol('ADMIN') || this.tieneRol('ENFERMER@');
  }

  cargarAtenciones(): void {
    this.atenciones = [];
    this.atencionesEnConsulta = [];

    this.cargarAtencionesEspera();

    this.cargarAtencionesEnConsulta();
  }

  // =========================================================
  // ATENCIONES EN ESPERA
  // =========================================================

  cargarAtencionesEspera(): void {
    this.atencionService.obtenerAtencionesEspera().subscribe({
      next: (atenciones) => {
        this.atenciones = atenciones; // sin mapear estadoDashboard
      },
      error: (error) => {
        // console.error('Error al obtener atenciones en espera:', error);
      },
    });
  }

  // =========================================================
  // ATENCIONES EN CONSULTA
  // =========================================================

  cargarAtencionesEnConsulta(): void {
    this.atencionService.obtenerAtencionesAtendidos().subscribe({
      next: (atenciones) => {
        this.atencionesEnConsulta = atenciones; // sin forzar 'EN_CONSULTA'
      },
      error: (error) => {
        // console.error('Error al obtener atenciones en consulta:', error);
      },
    });
  }

  // =========================================================
  // RECARGAR ATENCIONES
  // =========================================================

  recargarAtenciones(): void {
    // console.log('========== RECARGANDO ATENCIONES ==========');

    this.cargarAtenciones();
  }

  // =========================================================
  // BUSCAR PACIENTES
  // =========================================================

  /**
   * Normaliza el texto para ignorar mayúsculas, acentos
   * y espacios repetidos durante la búsqueda.
   */
  private normalizarTexto(valor: unknown): string {
    return String(valor ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
  }

  /**
   * Filtra pacientes usando el valor actual del input.
   */
  filtrarPacientes(valor?: string): void {
    // Utiliza directamente el texto del evento cuando está disponible.
    // Esto evita depender del orden de actualización de ngModel.
    if (valor !== undefined) {
      this.busqueda = valor;
    }

    const termino = this.normalizarTexto(this.busqueda);

    if (!termino) {
      this.pacientesFiltrados = [...this.pacientes];
      return;
    }

    this.pacientesFiltrados = this.pacientes.filter((paciente) => {
      const nombreCompleto = this.normalizarTexto(
        [paciente.nombre, paciente.apellido, paciente.segundoApellido, paciente.telefono]
          .filter(Boolean)
          .join(' '),
      );

      return nombreCompleto.includes(termino);
    });
  }

  // =========================================================
  // CANCELAR ATENCIÓN
  // =========================================================

  abrirPacientesAtendidos(): void {
    // console.log('Atenciones en consulta:', this.atencionesEnConsulta);

    this.dialog.open(DashboardAtendidos, {
      width: '65vw',
      maxWidth: '95vw',
      minWidth: 'min(95vw, 420px)',
      maxHeight: '90vh',
      data: {
        atendidos: this.atencionesEnConsulta,
        rol: this.user?.rol?.nombre,
      },
    });
  }

  abrirTodosPendientes(): void {
    const dialogRef = this.dialog.open(DashboardPendientes, {
      width: '65vw',
      maxWidth: '95vw',
      minWidth: 'min(95vw, 570px)',
      maxHeight: '90vh',
      data: { pendientes: this.atenciones, rol: this.user?.rol?.nombre },
    });
    dialogRef.afterClosed().subscribe((resultado) => {
      // console.log('Resultado DashboardPendientes:', resultado);
      if (resultado?.cancelada || resultado?.actualizar) {
        // console.log('========== ACTUALIZANDO ATENCIONES ==========');
        this.cargarAtenciones();
      }
    });
  }

  abrirNuevoPaciente(): void {
    const dialogRef = this.dialog.open(NuevoPaciente, {
      width: '95vw',
      height: '90vh',
      maxWidth: '95vw',
      data: {
        modo: 'nuevo',
      },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (!resultado) {
        return;
      }

      this.cargarPacientes();

      if (resultado.abrirSignosVitales && resultado.paciente) {
        this.abrirRegistroSignosVitales(resultado.paciente);
      }
    });
  }

  abrirRegistroSignosVitales(paciente: Paciente): void {
    const dialogRef = this.dialog.open(RegistroSignosVitales, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      data: paciente,
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (resultado) {
        this.cargarAtenciones();
      }
    });
  }
}
